import { RouteRecordRaw } from 'vue-router';
import { RouteMetaType, ExtendedRouteMetaType } from '@/router/routes/types';
import { ENTRY_POINTS } from '@constants/entry-points';

type SandboxRouteMetaType = RouteMetaType & {
  requiresAuth: false,
  requiresTenant: false,
  requiresTenantAdmin: false,
  requiresEnterpriseLicense: false,
};

const META_DEFAULT: SandboxRouteMetaType = Object.freeze({
  requiresAuth: false,
  requiresTenant: false,
  requiresTenantAdmin: false,
  requiresEnterpriseLicense: false,
  redirectOnError: ENTRY_POINTS.PATH.UNAUTHORIZED(),
});

const getMeta = (
  extendedMeta: ExtendedRouteMetaType<SandboxRouteMetaType> = {},
): SandboxRouteMetaType => ({ ...META_DEFAULT, ...extendedMeta });

const routes: Array<RouteRecordRaw> = [
  {
    path: '/CX1lsu63Bn/sandbox',
    name: 'sandbox-root',
    component: () => import('@views/sandbox/SandBoxRootView.vue'),
    redirect: { name: 'sandbox-debug' },
    meta: getMeta(),
    children: [
      {
        path: 'debug',
        component: () => import('@views/sandbox/SandBoxDebugView.vue'),
        meta: getMeta(),
        children: [
          {
            path: 'v1/',
            component: () => import('@views/sandbox/v1/SandBoxView.vue'),
            meta: getMeta(),
            children: [
              {
                path: 'button',
                name: 'v1-debug-button',
                component: () => import('@views/sandbox/v1/ButtonView.vue'),
                meta: getMeta({ title: 'Button | v1' }),
              },
              {
                path: 'card',
                name: 'v1-debug-card',
                component: () => import('@views/sandbox/v1/CardView.vue'),
                meta: getMeta({ title: 'Card | v1' }),
              },
              {
                path: 'checkbox',
                name: 'v1-debug-checkbox',
                component: () => import('@views/sandbox/v1/CheckboxView.vue'),
                meta: getMeta({ title: 'Checkbox | v1' }),
              },
              {
                path: 'dropdown',
                name: 'v1-debug-dropdown',
                component: () => import('@views/sandbox/v1/DropdownView.vue'),
                meta: getMeta({ title: 'Dropdown | v1' }),
              },
              {
                path: 'header',
                name: 'v1-debug-header',
                component: () => import('@views/sandbox/v1/HeaderView.vue'),
                meta: getMeta({ title: 'Header | v1' }),
              },
              {
                path: 'image',
                name: 'v1-debug-image',
                component: () => import('@views/sandbox/v1/ImageView.vue'),
                meta: getMeta({ title: 'Image | v1' }),
              },
              {
                path: 'indicator',
                name: 'v1-debug-indicator',
                component: () => import('@views/sandbox/v1/IndicatorView.vue'),
                meta: getMeta({ title: 'Indicator | v1' }),
              },
              {
                path: 'input',
                name: 'v1-debug-input',
                component: () => import('@views/sandbox/v1/InputView.vue'),
                meta: getMeta({ title: 'Input | v1' }),
              },
              {
                path: 'link',
                name: 'v1-debug-link',
                component: () => import('@views/sandbox/v1/LinkView.vue'),
                meta: getMeta({ title: 'Link | v1' }),
              },
              {
                path: 'loader',
                name: 'v1-debug-loader',
                component: () => import('@views/sandbox/v1/LoaderView.vue'),
                meta: getMeta({ title: 'Loader | v1' }),
              },
              {
                path: 'modal',
                name: 'v1-debug-modal',
                component: () => import('@views/sandbox/v1/ModalView.vue'),
                meta: getMeta({ title: 'Modal | v1' }),
              },
              {
                path: 'multi-toggle',
                name: 'v1-debug-multi-toggle',
                component: () => import('@views/sandbox/v1/MultiToggleView.vue'),
                meta: getMeta({ title: 'MultiToggle | v1' }),
              },
              {
                path: 'pagination',
                name: 'v1-debug-pagination',
                component: () => import('@views/sandbox/v1/PaginationView.vue'),
                meta: getMeta({ title: 'Pagination | v1' }),
              },
              {
                path: 'popover',
                name: 'v1-debug-popover',
                component: () => import('@views/sandbox/v1/PopoverView.vue'),
                meta: getMeta({ title: 'Popover | v1' }),
              },
              {
                path: 'stepper',
                name: 'v1-debug-stepper',
                component: () => import('@views/sandbox/v1/StepperView.vue'),
                meta: getMeta({ title: 'Stepper | v1' }),
              },
              {
                path: 'symbol',
                name: 'v1-debug-symbol',
                component: () => import('@views/sandbox/v1/SymbolView.vue'),
                meta: getMeta({ title: 'Symbol | v1' }),
              },
              {
                path: 'tab',
                name: 'v1-debug-tab',
                component: () => import('@views/sandbox/v1/TabView.vue'),
                meta: getMeta({ title: 'Tab | v1' }),
              },
              {
                path: 'tag',
                name: 'v1-debug-tag',
                component: () => import('@views/sandbox/v1/TagView.vue'),
                meta: getMeta({ title: 'Tag | v1' }),
              },
              {
                path: 'tag-input',
                name: 'v1-debug-tag-input',
                component: () => import('@views/sandbox/v1/TagInputView.vue'),
                meta: getMeta({ title: 'TagInput | v1' }),
              },
              {
                path: 'text',
                name: 'v1-debug-text',
                component: () => import('@views/sandbox/v1/TextView.vue'),
                meta: getMeta({ title: 'Text | v1' }),
              },
              {
                path: 'textarea',
                name: 'v1-debug-textarea',
                component: () => import('@views/sandbox/v1/TextareaView.vue'),
                meta: getMeta({ title: 'Textarea | v1' }),
              },
              {
                path: 'toast',
                name: 'v1-debug-toast',
                component: () => import('@views/sandbox/v1/ToastView.vue'),
                meta: getMeta({ title: 'Toast | v1' }),
              },
              {
                path: 'toggle',
                name: 'v1-debug-toggle',
                component: () => import('@views/sandbox/v1/ToggleView.vue'),
                meta: getMeta({ title: 'Toggle | v1' }),
              },
            ],
          },
          {
            path: 'v1/redirect',
            name: 'v1-debug-redirect',
            component: () => import('@views/sandbox/v1/DebugRoomRedirectView.vue'),
            meta: getMeta({ title: 'Redirected | v1' }),
          },
          {
            path: 'case-conversion',
            name: 'case-conversion',
            component: () => import('@views/sandbox/CaseConversionView.vue'),
            meta: getMeta({ title: 'Case Conversion' }),
          },
        ],
      },
    ],
  },
];

export default routes;
